import { IconBookmark } from 'central-icons/IconBookmark';
import { IconCrossLarge } from 'central-icons/IconCrossLarge';
import classNames from 'classnames';
import mixpanel from 'mixpanel-browser';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { SingleValue } from 'react-select';

import { SavedViewsEvents } from '@revelio/core';
import type { FilterSetScope } from '@revelio/iso-utility';
import {
  Button,
  IconButton,
  Link,
  Popover,
  RevelioTooltip,
  Spinner,
  Text,
  useDisclosure,
} from '@revelio/ui';

import type { Option } from '../../filters/types/filters';
import { ActionBarIconButton } from '../PageActionBar/ActionBarIconButton';
import { CreateSavedViewForm } from './CreateSavedViewForm';
import styles from './SavedViewsButton.module.css';
import { type SavedViewOption, SavedViewsList } from './SavedViewsList';
import layoutStyles from './saved-views-button.module.css';
import { type SavedView, useSavedViews } from './useSavedViews';

const PlaceholderBox = ({ children }: { children: React.ReactNode }) => (
  <div
    className={classNames(styles.placeholder, layoutStyles.placeholderContent)}
  >
    {children}
  </div>
);

const NO_FILTERS_TO_SAVE_MESSAGE = 'Add at least one filter to create a view';

export type SavedViewsButtonProps = {
  scope: FilterSetScope;
  canCreate: boolean;
  isDisabled?: boolean;
  onApplySavedView: (value: SingleValue<Option<SavedView>> | undefined) => void;
  /**
   * What a new set records. Defaults to the live URL, which is what a page
   * whose whole state is deeplinked wants; pass one to leave view-only params
   * out (the Economic Impact page drops its active tab this way).
   */
  buildUrl?: () => string;
  /** Shown where a page has nothing worth saving yet. */
  nothingToSaveMessage?: string;
  /**
   * Whether a set's stored url is a filter map the page can preview and
   * describe; turns on the list's hover preview and details card.
   */
  canPreview?: boolean;
};

/**
 * The saved-filter-sets bookmark: browse and apply, or name and create. Takes
 * everything it needs as props — no page context — so any page can mount it by
 * naming its scope, how it decides there is something to save, and what
 * applying a set does.
 */
export const SavedViewsButton = ({
  scope,
  canCreate,
  onApplySavedView,
  isDisabled,
  buildUrl,
  nothingToSaveMessage = NO_FILTERS_TO_SAVE_MESSAGE,
  canPreview = false,
}: SavedViewsButtonProps) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [stage, setStage] = useState<'browse' | 'create'>('browse');

  // What a set records, whether it is created from the form or overwritten
  // from a row's menu: both store the same url.
  const currentUrl = () =>
    buildUrl?.() ?? `${window.location.pathname}${window.location.search}`;

  useEffect(() => {
    if (!canCreate && stage === 'create') {
      setStage('browse');
    }
  }, [canCreate, stage]);

  // The surface portals to the end of <body>, so the create form's name field
  // is looked up from the popover body rather than from the whole document.
  const bodyRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (stage === 'create') {
      setTimeout(() => {
        bodyRef.current?.querySelector('input')?.focus();
      }, 0);
    }
  }, [stage]);

  const {
    options: savedViews,
    fetchingSavedViews,
    canManageViews,
  } = useSavedViews();
  const showCreate = stage === 'create' && canManageViews;

  const track = (
    event: SavedViewsEvents,
    item?: { id: string; label: string }
  ) => {
    mixpanel.track(event, { scope, ...item });
  };

  const trackItem = (event: SavedViewsEvents) => (item: SavedView) =>
    track(event, { id: item.id, label: item.name });

  const compatibleOptions: SavedViewOption[] = useMemo(() => {
    return savedViews
      .filter((set) => set.scope === scope)
      .map((set) => ({
        value: set.id,
        label: set.name,
        entity: set,
      }));
  }, [savedViews, scope]);

  const handleApply = (
    selected: SingleValue<Option<SavedView>> | undefined,
    close: () => void
  ) => {
    onApplySavedView(selected);
    close();

    if (selected) {
      track(SavedViewsEvents.APPLY, {
        id: selected.value,
        label: selected.label,
      });
    }
  };

  const handleCreate = trackItem(SavedViewsEvents.CREATE);
  const handleOverwrite = trackItem(SavedViewsEvents.OVERWRITE);
  const handleUpdate = trackItem(SavedViewsEvents.RENAME);
  const handleDelete = trackItem(SavedViewsEvents.DELETE);

  const goToCreateStage = () => setStage('create');
  const goToBrowseStage = () => {
    track(SavedViewsEvents.OPEN);
    setStage('browse');
    onOpen();
  };

  return (
    <Popover
      open={isOpen}
      onOpenChange={(next) => (next ? goToBrowseStage() : onClose())}
      placement="bottom-end"
      className={styles.popover}
      onOpenAutoFocus={(event) => event.preventDefault()}
      onCloseAutoFocus={(event) => event.preventDefault()}
      trigger={
        <ActionBarIconButton
          data-testid="saved-view-menu-trigger"
          icon={IconBookmark}
          aria-label="Saved views"
          tooltipLabel="Saved Views"
          isDisabled={isDisabled}
        />
      }
    >
      {({ close }) => (
        <>
          <div className={styles.header}>
            <div className={layoutStyles.header}>
              <Text as="h2" fontSize="sm" weight="semibold" tone="inherit">
                {showCreate ? 'New' : 'Saved Views'}
              </Text>
              <div className={layoutStyles.headerActions}>
                {fetchingSavedViews && (
                  <div className={layoutStyles.loadingIndicator}>
                    <Spinner size="xs" />
                  </div>
                )}
                {canManageViews && (
                  <RevelioTooltip
                    label={nothingToSaveMessage}
                    isDisabled={canCreate}
                  >
                    {showCreate ? (
                      <IconButton
                        data-testid="saved-view-stage-toggle"
                        aria-label="Back to saved views"
                        icon={<IconCrossLarge size={14} />}
                        size="sm"
                        shape="squared"
                        variant="ghost"
                        onClick={goToBrowseStage}
                      />
                    ) : (
                      <Button
                        data-testid="saved-view-stage-toggle"
                        className={styles.addNew}
                        size="sm"
                        variant="outlined"
                        disabled={!canCreate}
                        onClick={goToCreateStage}
                      >
                        Add New
                      </Button>
                    )}
                  </RevelioTooltip>
                )}
              </div>
            </div>
          </div>
          <div ref={bodyRef} className={styles.body}>
            {!canManageViews && (
              <Text
                className={classNames(styles.muted, layoutStyles.readOnlyHint)}
                fontSize="x-sm"
                data-testid="saved-view-read-only-hint"
              >
                Read-only while impersonating
              </Text>
            )}
            {!showCreate && (
              <SavedViewsList
                options={compatibleOptions}
                onApply={(selected) => handleApply(selected, close)}
                onUpdated={handleUpdate}
                onDeleted={handleDelete}
                onOverwritten={handleOverwrite}
                currentUrl={currentUrl}
                canOverwrite={canCreate}
                canPreview={canPreview}
                emptySlot={
                  <PlaceholderBox>
                    <div
                      className={classNames(
                        styles.muted,
                        layoutStyles.emptyMessage
                      )}
                    >
                      No compatible views found
                    </div>
                    {canManageViews &&
                      (canCreate ? (
                        <Link
                          color="accent"
                          className={layoutStyles.addNewLink}
                          onClick={goToCreateStage}
                        >
                          Add New
                        </Link>
                      ) : (
                        <Text
                          className={classNames(
                            styles.muted,
                            styles.confirmText
                          )}
                          fontSize="sm"
                          data-testid="saved-view-empty-hint"
                        >
                          {nothingToSaveMessage}
                        </Text>
                      ))}
                  </PlaceholderBox>
                }
                deleteSlot={(opt) => (
                  <PlaceholderBox>
                    <Text fontSize="sm" className={styles.confirmText}>
                      Are you sure you want to delete <b>{opt.label}</b> saved
                      view? This action cannot be undone.
                    </Text>
                  </PlaceholderBox>
                )}
                overwriteSlot={(opt) => (
                  <PlaceholderBox>
                    <Text fontSize="sm" className={styles.confirmText}>
                      Are you sure you want to overwrite <b>{opt.label}</b>{' '}
                      saved view? This action cannot be undone.
                    </Text>
                  </PlaceholderBox>
                )}
              />
            )}

            {showCreate && (
              <CreateSavedViewForm
                scope={scope}
                isDisabled={!canCreate}
                currentUrl={currentUrl}
                onClose={goToBrowseStage}
                onCreated={handleCreate}
                onOverwritten={handleOverwrite}
                overwriteSlot={(item) => (
                  <PlaceholderBox>
                    <Text fontSize="sm" className={styles.confirmText}>
                      Are you sure you want to overwrite <b>{item.name}</b>{' '}
                      saved view?
                    </Text>
                  </PlaceholderBox>
                )}
              />
            )}
          </div>
        </>
      )}
    </Popover>
  );
};
